import './TwoStageBar.scss';

interface CredibleInterval {
  lo: number;
  hi: number;
}

interface TwoStageBarProps {
  direct: number;
  actualized: number;
  maxValue: number;
  color: string;
  spendOpacity?: number;
  credibleInterval?: CredibleInterval;
}

// A channel's direct → actualized bar: solid up to the smaller of the two, then the two-stage
// difference — striped channel color where credit was added, channel-color stripes on white where
// it was reduced — split from the solid portion by a thin white divider. Shared by the Channel
// Performance bar view and the drawer's Inter-Channel Effects chart.
export const TwoStageBar = ({
  direct,
  actualized,
  maxValue,
  color,
  spendOpacity = 1,
  credibleInterval
}: TwoStageBarProps) => {
  if (maxValue <= 0) return <div className='two-stage-bar' />;
  const directPct = (direct / maxValue) * 100;
  const actualizedPct = (actualized / maxValue) * 100;
  const minPct = Math.min(directPct, actualizedPct);
  const deltaPct = Math.abs(directPct - actualizedPct);
  const isAdded = actualized > direct;
  const isReduced = direct > actualized;
  const ciLoPct = credibleInterval ? Math.max(0, (credibleInterval.lo / maxValue) * 100) : 0;
  const ciHiPct = credibleInterval ? Math.min(100, (credibleInterval.hi / maxValue) * 100) : 0;
  const ciWidthPct = Math.max(0, ciHiPct - ciLoPct);

  return (
    <div className='two-stage-bar'>
      <div
        className='two-stage-bar-solid'
        style={{ width: `${minPct}%`, backgroundColor: color, opacity: spendOpacity }}
      />
      {isAdded && deltaPct > 0 && (
        <div
          className='two-stage-bar-added'
          style={{
            left: `${minPct}%`,
            width: `${deltaPct}%`,
            backgroundColor: color,
            opacity: spendOpacity
          }}
        />
      )}
      {isReduced && deltaPct > 0 && (
        <div
          className='two-stage-bar-reduced'
          style={{
            left: `${minPct}%`,
            width: `${deltaPct}%`,
            color
          }}
        />
      )}
      {deltaPct > 0 && <div className='two-stage-bar-divider' style={{ left: `${minPct}%` }} />}
      {credibleInterval && ciWidthPct > 0 && (
        <div className='two-stage-bar-ci' style={{ left: `${ciLoPct}%`, width: `${ciWidthPct}%` }}>
          <span className='two-stage-bar-ci-cap left' />
          <span className='two-stage-bar-ci-bar' />
          <span className='two-stage-bar-ci-cap right' />
        </div>
      )}
    </div>
  );
};
